import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Calendar, CheckCircle, XCircle, Clock, ChevronLeft, ChevronRight, Download, FileText, Edit, Plus, Trash2, Bell, TrendingUp, TrendingDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Navbar from '../components/Navbar';
import HolidayManager from '../components/HolidayManager';
import ClientErrorsLog from '../components/ClientErrorsLog';
import api from '../api/axios';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { safeFormat } from '../lib/utils';

// IST (UTC+5:30) today as YYYY-MM-DD
const istTodayStr = () => new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);

const AdminDashboard = ({ user, onLogout }) => {
  const [employees, setEmployees] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [todayAttendance, setTodayAttendance] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(istTodayStr());
  const [showTodayAttendance, setShowTodayAttendance] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportMonth, setReportMonth] = useState(new Date().getMonth() + 1);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [useCustomDateRange, setUseCustomDateRange] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showEmployeesList, setShowEmployeesList] = useState(false);
  const [showPendingLeaves, setShowPendingLeaves] = useState(false);
  const [showApprovedLeaves, setShowApprovedLeaves] = useState(false);
  
  // Edit Attendance State
  const [showEditAttendance, setShowEditAttendance] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editCheckIn, setEditCheckIn] = useState('');
  const [editCheckOut, setEditCheckOut] = useState('');
  const [editSessionIndex, setEditSessionIndex] = useState(0);
  
  // Add Attendance State
  const [showAddAttendance, setShowAddAttendance] = useState(false);
  const [addUserId, setAddUserId] = useState('');
  const [addDate, setAddDate] = useState('');
  const [addCheckIn, setAddCheckIn] = useState('');
  const [addCheckOut, setAddCheckOut] = useState('');
  
  // Notifications State
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  
  // Overtime State
  const [overtimeMonth, setOvertimeMonth] = useState(new Date().getMonth() + 1);
  const [overtimeYear, setOvertimeYear] = useState(new Date().getFullYear());
  const [overtimePreview, setOvertimePreview] = useState(null);
  const [overtimeLoading, setOvertimeLoading] = useState(false);

  // Bulk Attendance State
  const [showBulkAttendance, setShowBulkAttendance] = useState(false);

  // Leave Summary State
  const [leaveSummary, setLeaveSummary] = useState(null);
  const [leaveSummaryLoading, setLeaveSummaryLoading] = useState(false);
  const [expandedEmployee, setExpandedEmployee] = useState(null);
  const [bulkUserId, setBulkUserId] = useState('');
  const [bulkMonth, setBulkMonth] = useState(new Date().getMonth() + 1);
  const [bulkYear, setBulkYear] = useState(new Date().getFullYear());
  const [bulkFromDate, setBulkFromDate] = useState('');
  const [bulkToDate, setBulkToDate] = useState('');
  const [bulkSession1In, setBulkSession1In] = useState('10:15');
  const [bulkSession1Out, setBulkSession1Out] = useState('13:30');
  const [bulkSession2In, setBulkSession2In] = useState('15:00');
  const [bulkSession2Out, setBulkSession2Out] = useState('19:00');
  const [bulkIncludeSundays, setBulkIncludeSundays] = useState('');
  const [bulkSundayIn, setBulkSundayIn] = useState('10:00');
  const [bulkSundayOut, setBulkSundayOut] = useState('13:45');
  const [bulkLoading, setBulkLoading] = useState(false);

  useEffect(() => {
    fetchData();
    fetchNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        api.get('/admin/notifications'),
        api.get('/admin/notifications/unread-count')
      ]);
      setNotifications(notifRes.data);
      setUnreadCount(countRes.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.post(`/admin/notifications/${notificationId}/read`);
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.post('/admin/notifications/mark-all-read');
      fetchNotifications();
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const fetchData = async () => {
    try {
      const today = istTodayStr();
      const sel = selectedDate || today;
      const [employeesRes, leavesRes, todayRes, selRes] = await Promise.all([
        api.get('/admin/employees'),
        api.get('/admin/leaves'),
        api.get(`/admin/attendance?date=${today}`),
        api.get(`/admin/attendance?date=${sel}`),
      ]);
      setEmployees(employeesRes.data);
      setLeaves(leavesRes.data);
      setTodayAttendance(todayRes.data);
      setAttendance(selRes.data);
    } catch (error) {
      console.error('Failed to fetch data');
    }
  };

  const fetchAttendance = async (date) => {
    try {
      const res = await api.get(`/admin/attendance?date=${date}`);
      setAttendance(res.data);
    } catch (error) {
      console.error('Failed to fetch attendance');
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    fetchAttendance(date);
  };

  const handleApprove = async (leaveId) => {
    setLoading(true);
    try {
      await api.post(`/admin/leaves/${leaveId}/approve`);
      toast.success('Leave approved successfully!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve leave');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (leaveId) => {
    setLoading(true);
    try {
      await api.post(`/admin/leaves/${leaveId}/reject`);
      toast.success('Leave rejected successfully!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject leave');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLeave = async (leave) => {
    const days = Math.ceil((new Date(leave.end_date) - new Date(leave.start_date)) / (1000 * 60 * 60 * 24)) + 1;
    if (!window.confirm(`Delete leave for ${leave.user_name}?\n\n${leave.leave_type}: ${leave.start_date} to ${leave.end_date} (${days} days)\nStatus: ${leave.status}\n\nIf this was an approved paid leave, the leave balance will be restored.`)) {
      return;
    }
    setLoading(true);
    try {
      const res = await api.delete(`/admin/leaves/${leave.leave_id}`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete leave');
    } finally {
      setLoading(false);
    }
  };

  // Edit Attendance Functions
  const openEditAttendance = (record, sessionIndex = 0) => {
    setEditingRecord(record);
    setEditSessionIndex(sessionIndex);
    const session = record.sessions[sessionIndex];
    
    // Convert to local datetime format for input
    if (session?.check_in) {
      const checkInDate = new Date(session.check_in);
      setEditCheckIn(checkInDate.toISOString().slice(0, 16));
    } else {
      setEditCheckIn('');
    }
    
    if (session?.check_out) {
      const checkOutDate = new Date(session.check_out);
      setEditCheckOut(checkOutDate.toISOString().slice(0, 16));
    } else {
      setEditCheckOut('');
    }
    
    setShowEditAttendance(true);
  };

  const handleUpdateAttendance = async () => {
    if (!editingRecord) return;
    
    setLoading(true);
    try {
      await api.put('/admin/attendance/update', {
        user_id: editingRecord.user_id,
        date: editingRecord.date,
        session_index: editSessionIndex,
        check_in: editCheckIn ? new Date(editCheckIn).toISOString() : null,
        check_out: editCheckOut ? new Date(editCheckOut).toISOString() : null
      });
      toast.success('Attendance updated successfully!');
      setShowEditAttendance(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update attendance');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSession = async (record, sessionIndex) => {
    if (!window.confirm('Are you sure you want to delete this session?')) return;
    
    setLoading(true);
    try {
      await api.delete(`/admin/attendance/delete-session?user_id=${record.user_id}&date=${record.date}&session_index=${sessionIndex}`);
      toast.success('Session deleted successfully!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete session');
    } finally {
      setLoading(false);
    }
  };

  const openAddAttendance = () => {
    setAddUserId('');
    setAddDate(selectedDate);
    setAddCheckIn('');
    setAddCheckOut('');
    setShowAddAttendance(true);
  };

  const handleAddAttendance = async () => {
    if (!addUserId || !addDate || !addCheckIn) {
      toast.error('Please fill in employee, date, and check-in time');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/admin/attendance/add', {
        user_id: addUserId,
        date: addDate,
        check_in: new Date(addCheckIn).toISOString(),
        check_out: addCheckOut ? new Date(addCheckOut).toISOString() : null
      });
      toast.success('Attendance added successfully!');
      setShowAddAttendance(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add attendance');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    try {
      return format(new Date(isoString), 'hh:mm a');
    } catch {
      return '-';
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'dd MMM yyyy');
    } catch {
      return dateStr;
    }
  };

  const changeDate = (days) => {
    const current = new Date(selectedDate + 'T00:00:00Z');
    current.setDate(current.getDate() + days);
    const next = current.toISOString().split('T')[0];
    setSelectedDate(next);
    fetchAttendance(next);
  };

  // Filter attendance by selected date
  const filteredAttendance = attendance.filter(a => a.date === selectedDate);

  const pendingLeaves = leaves.filter((l) => l.status === 'pending');

  // Generate monthly report
  const generateReport = async () => {
    setReportLoading(true);
    try {
      let response;
      if (useCustomDateRange && startDate && endDate) {
        response = await api.get(`/admin/report-by-daterange?start_date=${startDate}&end_date=${endDate}`);
      } else {
        response = await api.get(`/admin/monthly-report/${reportYear}/${reportMonth}`);
      }
      setReportData(response.data);
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setReportLoading(false);
    }
  };

  // Download report as CSV
  const downloadReport = async () => {
    try {
      let url;
      let filename;
      
      if (useCustomDateRange && startDate && endDate) {
        // Use backend endpoint for custom date range
        url = `/admin/report-by-daterange/download?start_date=${startDate}&end_date=${endDate}`;
        filename = `payroll_${startDate}_to_${endDate}.csv`;
      } else {
        url = `/admin/monthly-report/${reportYear}/${reportMonth}/download`;
        filename = `payroll_${monthNames[reportMonth - 1]}_${reportYear}.csv`;
      }
      
      const response = await api.get(url, {
        responseType: 'blob'
      });
      
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Report downloaded successfully!');
    } catch (error) {
      toast.error('Failed to download report');
    }
  };

  // Generate CSV content for custom date range
  const generateCSVContent = () => {
    if (!reportData) return '';
    
    let csv = 'S.N. Randar & Associates - Attendance Report\n';
    csv += `Period: ${startDate} to ${endDate}\n`;
    csv += `Working Days: ${reportData.working_days_in_range || reportData.working_days_in_month}\n`;
    csv += `Company Holidays: ${reportData.company_holidays}\n`;
    csv += `Total Employees: ${reportData.total_employees}\n\n`;
    csv += 'Employee Name,Email,Working Days,Days Worked,Hours Worked,Paid Leaves,Sick Leaves,Other Leaves,Total Leaves,Paid Leave Balance,Unpaid/Extra Leaves,Absent Days\n';
    
    reportData.report.forEach(row => {
      csv += `${row.employee_name},${row.email},${row.total_working_days},${row.days_worked},${row.total_hours_worked},${row.paid_leaves_used},${row.sick_leaves_used},${row.other_leaves_used},${row.total_leaves_used},${row.total_paid_leaves_balance},${row.extra_unpaid_leaves},${row.absent_days}\n`;
    });
    
    return csv;
  };

  // Download individual employee attendance
  const downloadEmployeeAttendance = async (userId, employeeName) => {
    try {
      const sDate = useCustomDateRange && startDate ? startDate : `${reportYear}-${String(reportMonth).padStart(2, '0')}-01`;
      const eDate = useCustomDateRange && endDate ? endDate : `${reportYear}-${String(reportMonth).padStart(2, '0')}-${new Date(reportYear, reportMonth, 0).getDate()}`;
      
      const response = await api.get(`/admin/employee-attendance/${userId}?start_date=${sDate}&end_date=${eDate}`, {
        responseType: 'blob'
      });
      
      const safeName = employeeName.replace(/\s+/g, '_');
      const filename = `attendance_${safeName}_${sDate}_to_${eDate}.csv`;
      
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(`Downloaded ${employeeName}'s attendance`);
    } catch (error) {
      toast.error('Failed to download employee attendance');
    }
  };

  const monthNames = ["January", "February", "March", "April", "May", "June", 
                      "July", "August", "September", "October", "November", "December"];

  // Overtime functions
  const fetchOvertimePreview = async () => {
    setOvertimeLoading(true);
    try {
      const response = await api.get(`/admin/overtime-preview/${overtimeYear}/${overtimeMonth}`);
      setOvertimePreview(response.data);
    } catch (error) {
      toast.error('Failed to fetch overtime preview');
    } finally {
      setOvertimeLoading(false);
    }
  };

  const processOvertime = async () => {
    if (!window.confirm(`Are you sure you want to process overtime for ${monthNames[overtimeMonth - 1]} ${overtimeYear}? This will adjust paid leave balances for ALL employees.`)) {
      return;
    }
    
    setOvertimeLoading(true);
    try {
      const response = await api.post(`/admin/process-overtime/${overtimeYear}/${overtimeMonth}`);
      toast.success(`Processed overtime for ${response.data.employees_processed} employees`);
      fetchOvertimePreview(); // Refresh preview
      fetchData(); // Refresh employee data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process overtime');
    } finally {
      setOvertimeLoading(false);
    }
  };

  const processIndividualOvertime = async (userId, employeeName) => {
    if (!window.confirm(`Apply overtime adjustment for ${employeeName} for ${monthNames[overtimeMonth - 1]} ${overtimeYear}?`)) {
      return;
    }
    
    setOvertimeLoading(true);
    try {
      const response = await api.post(`/admin/process-overtime/${overtimeYear}/${overtimeMonth}/${userId}`);
      toast.success(`Processed overtime for ${response.data.employee_name}: ${response.data.leave_adjustment >= 0 ? '+' : ''}${response.data.leave_adjustment} leave`);
      fetchOvertimePreview(); // Refresh preview
      fetchData(); // Refresh employee data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process overtime');
    } finally {
      setOvertimeLoading(false);
    }
  };

  const removeEmployee = async (userId, employeeName) => {
    if (!window.confirm(`Are you sure you want to remove "${employeeName}"? This will delete ALL their data (attendance, leaves, etc.). This action cannot be undone.`)) {
      return;
    }
    if (!window.confirm(`FINAL CONFIRMATION: Remove ${employeeName} permanently?`)) {
      return;
    }
    
    try {
      await api.delete(`/admin/employees/${userId}`);
      toast.success(`${employeeName} has been removed`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove employee');
    }
  };

  const resetAndReprocessOvertime = async (userId, employeeName) => {
    if (!window.confirm(`Reset & Reprocess overtime for ${employeeName} for ${monthNames[overtimeMonth - 1]} ${overtimeYear}? This will reset their leave balance to 15 and recalculate.`)) {
      return;
    }

    setOvertimeLoading(true);
    try {
      const response = await api.post(`/admin/reset-overtime/${overtimeYear}/${overtimeMonth}/${userId}`);
      toast.success(`${response.data.message}`);
      fetchOvertimePreview();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset overtime');
    } finally {
      setOvertimeLoading(false);
    }
  };

  const debugAndFixLeaves = async (userId, employeeName) => {
    try {
      // First debug - show what's wrong
      const debugRes = await api.get(`/admin/debug-leaves/${userId}`);
      const data = debugRes.data;
      
      let msg = `${employeeName}'s Leaves:\n\n`;
      msg += `Total in DB: ${data.total_leaves_in_db}\n`;
      msg += `Approved leaves this year: ${data.total_approved_leaves_this_year}\n`;
      msg += `Counted as paid: ${data.total_counted_as_paid}\n\n`;
      
      data.leaves.forEach(l => {
        msg += `${l.leave_type} | ${l.start_date} to ${l.end_date} | ${l.days}d | category="${l.leave_category}" | counted=${l.counted_as_paid}\n`;
      });
      
      // Check if there are bad leaves (no category)
      const badLeaves = data.leaves.filter(l => !l.leave_category || l.leave_category === 'NOT SET');
      
      if (badLeaves.length > 0) {
        msg += `\n${badLeaves.length} leaves have no category set. Delete them?`;
        if (window.confirm(msg)) {
          const fixRes = await api.delete(`/admin/fix-leaves/${userId}`);
          toast.success(fixRes.data.message);
          fetchData();
        }
      } else {
        alert(msg);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to debug leaves');
    }
  };

  const handleMigrateLeaves = async () => {
    if (!window.confirm('This will:\n\n1. Set proper category (paid/unpaid) on all old leaves\n2. Reset all employee balances to 15\n3. Recalculate based on actual paid leaves used\n\nProceed?')) {
      return;
    }
    setLoading(true);
    try {
      const response = await api.post('/admin/migrate-leaves');
      let msg = response.data.message + '\n\nEmployee Balances:\n';
      response.data.adjustments.forEach(a => {
        msg += `${a.name}: ${a.paid_leaves_used} paid leaves used → Balance: ${a.new_balance}${a.had_overtime ? ' (needs overtime reprocess)' : ''}\n`;
      });
      alert(msg);
      toast.success('Migration complete!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Migration failed');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaveSummary = async () => {
    setLeaveSummaryLoading(true);
    try {
      const response = await api.get('/admin/leave-summary');
      setLeaveSummary(response.data);
    } catch (error) {
      toast.error('Failed to load leave summary');
    } finally {
      setLeaveSummaryLoading(false);
    }
  };


  const handleBulkAddAttendance = async () => {
    if (!bulkUserId) {
      toast.error('Please select an employee');
      return;
    }
    
    const sessions = [
      { check_in_ist: bulkSession1In, check_out_ist: bulkSession1Out },
      { check_in_ist: bulkSession2In, check_out_ist: bulkSession2Out }
    ];
    
    const includeSundays = bulkIncludeSundays
      .split(',')
      .map(s => s.trim())
      .filter(s => s.match(/^\d{4}-\d{2}-\d{2}$/));
    
    const sundaySessions = includeSundays.length > 0
      ? [{ check_in_ist: bulkSundayIn, check_out_ist: bulkSundayOut }]
      : [];
    
    const empName = employees.find(e => e.user_id === bulkUserId)?.name || 'Employee';
    if (!window.confirm(`Add attendance for ${empName} for missing weekdays${bulkFromDate ? ` from ${bulkFromDate}` : ''}${bulkToDate ? ` to ${bulkToDate}` : ''} in ${monthNames[bulkMonth - 1]} ${bulkYear}?`)) {
      return;
    }
    
    setBulkLoading(true);
    try {
      const response = await api.post('/admin/bulk-add-attendance', {
        user_id: bulkUserId,
        year: bulkYear,
        month: bulkMonth,
        from_date: bulkFromDate || null,
        to_date: bulkToDate || null,
        sessions,
        include_sundays: includeSundays,
        sunday_sessions: sundaySessions
      });
      toast.success(response.data.message);
      setShowBulkAttendance(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add bulk attendance');
    } finally {
      setBulkLoading(false);
    }
  };

  // Get current month's leaves
  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();
  
  const monthlyPendingLeaves = leaves.filter(l => {
    if (l.status !== 'pending') return false;
    const leaveDate = new Date(l.start_date);
    return leaveDate.getMonth() + 1 === currentMonth && leaveDate.getFullYear() === currentYear;
  });
  
  const monthlyApprovedLeaves = leaves.filter(l => {
    if (l.status !== 'approved') return false;
    const leaveDate = new Date(l.start_date);
    return leaveDate.getMonth() + 1 === currentMonth && leaveDate.getFullYear() === currentYear;
  });

  return (
    <div className="min-h-screen bg-background">
      <Navbar user={user} onLogout={onLogout} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold font-heading tracking-tight mb-2">
                Admin Dashboard
              </h1>
              <p className="text-lg text-muted-foreground">Manage employees, attendance, and leaves</p>
            </div>
            
            {/* Notification Bell */}
            <div className="relative">
              <Button
                variant="outline"
                size="icon"
                className="relative"
                onClick={() => setShowNotifications(!showNotifications)}
              >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>
              
              {/* Notification Dropdown */}
              {showNotifications && (
                <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-slate-200 z-50 max-h-96 overflow-y-auto">
                  <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                    <h3 className="font-semibold">Notifications</h3>
                    {unreadCount > 0 && (
                      <button
                        onClick={markAllAsRead}
                        className="text-sm text-primary hover:underline"
                      >
                        Mark all as read
                      </button>
                    )}
                  </div>
                  <div>
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center text-muted-foreground">
                        No notifications
                      </div>
                    ) : (
                      notifications.slice(0, 10).map((notif) => (
                        <div
                          key={notif.notification_id}
                          className={`p-4 border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${
                            !notif.read ? 'bg-blue-50' : ''
                          }`}
                          onClick={() => {
                            markAsRead(notif.notification_id);
                            // Navigate to leaves tab if it's a leave notification
                            if (notif.type === 'leave_application') {
                              setShowNotifications(false);
                            }
                          }}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-full ${
                              notif.type === 'leave_application' ? 'bg-amber-100' :
                              notif.type === 'leave_approved' ? 'bg-green-100' :
                              'bg-red-100'
                            }`}>
                              <Calendar className={`h-4 w-4 ${
                                notif.type === 'leave_application' ? 'text-amber-600' :
                                notif.type === 'leave_approved' ? 'text-green-600' :
                                'text-red-600'
                              }`} />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-sm">{notif.title}</p>
                              <p className="text-sm text-muted-foreground">{notif.message}</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {new Date(notif.created_at).toLocaleString('en-IN')}
                              </p>
                            </div>
                            {!notif.read && (
                              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div 
              className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 cursor-pointer hover:border-primary hover:shadow-md transition-all"
              onClick={() => setShowEmployeesList(!showEmployeesList)}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-muted-foreground">Total Employees</h3>
                <Users className="h-5 w-5 text-accent" />
              </div>
              <p className="text-3xl font-bold" data-testid="total-employees">
                {employees.length}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Click to view list</p>
            </div>

            <div 
              className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 cursor-pointer hover:border-primary hover:shadow-md transition-all"
              onClick={() => setShowTodayAttendance(!showTodayAttendance)}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-muted-foreground">Today's Attendance</h3>
                <CheckCircle className="h-5 w-5 text-success" />
              </div>
              <p className="text-3xl font-bold" data-testid="today-attendance">
                {todayAttendance.length} <span className="text-sm font-normal text-muted-foreground">/ {employees.length}</span>
              </p>
              <p className="text-xs text-muted-foreground mt-1">Click to view details</p>
            </div>

            <div 
              className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 cursor-pointer hover:border-primary hover:shadow-md transition-all"
              onClick={() => setShowPendingLeaves(!showPendingLeaves)}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-muted-foreground">Pending Leaves</h3>
                <Clock className="h-5 w-5 text-warning" />
              </div>
              <p className="text-3xl font-bold" data-testid="pending-leaves-count">
                {monthlyPendingLeaves.length}
              </p>
              <p className="text-xs text-muted-foreground mt-1">{monthNames[currentMonth - 1]} - Click to view</p>
            </div>

            <div 
              className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 cursor-pointer hover:border-primary hover:shadow-md transition-all"
              onClick={() => setShowApprovedLeaves(!showApprovedLeaves)}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-muted-foreground">Total Leaves</h3>
                <Calendar className="h-5 w-5 text-accent" />
              </div>
              <p className="text-3xl font-bold">{monthlyApprovedLeaves.length}</p>
              <p className="text-xs text-muted-foreground mt-1">{monthNames[currentMonth - 1]} - Click to view</p>
            </div>
          </div>

          {/* Employees List Panel */}
          {showEmployeesList && (
            <div className="mb-8 bg-white rounded-lg border border-slate-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold font-heading tracking-tight">
                  All Employees ({employees.length})
                </h2>
                <Button variant="outline" size="sm" onClick={() => setShowEmployeesList(false)}>
                  Close
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {employees.map((emp, index) => (
                  <div 
                    key={emp.user_id}
                    className="flex items-center gap-3 p-3 rounded-md border bg-slate-50"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{emp.name}</p>
                      <p className="text-xs text-muted-foreground">{emp.email}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pending Leaves Panel */}
          {showPendingLeaves && (
            <div className="mb-8 bg-white rounded-lg border border-slate-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold font-heading tracking-tight">
                  Pending Leave Requests - {monthNames[currentMonth - 1]} {currentYear}
                </h2>
                <Button variant="outline" size="sm" onClick={() => setShowPendingLeaves(false)}>
                  Close
                </Button>
              </div>
              {monthlyPendingLeaves.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">No pending leave requests for this month</p>
              ) : (
                <div className="space-y-3">
                  {monthlyPendingLeaves.map((leave) => (
                    <div 
                      key={leave.leave_id}
                      className="flex items-center justify-between p-3 rounded-md border bg-amber-50 border-amber-200"
                    >
                      <div>
                        <p className="font-medium">{leave.user_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {leave.leave_type} • {leave.start_date} to {leave.end_date}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">{leave.reason}</p>
                      </div>
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                        Pending
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Approved Leaves Panel */}
          {showApprovedLeaves && (
            <div className="mb-8 bg-white rounded-lg border border-slate-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold font-heading tracking-tight">
                  Approved Leaves - {monthNames[currentMonth - 1]} {currentYear}
                </h2>
                <Button variant="outline" size="sm" onClick={() => setShowApprovedLeaves(false)}>
                  Close
                </Button>
              </div>
              {monthlyApprovedLeaves.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">No approved leaves for this month</p>
              ) : (
                <div className="space-y-3">
                  {monthlyApprovedLeaves.map((leave) => (
                    <div 
                      key={leave.leave_id}
                      className="flex items-center justify-between p-3 rounded-md border bg-emerald-50 border-emerald-200"
                    >
                      <div>
                        <p className="font-medium">{leave.user_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {leave.leave_type} • {leave.start_date} to {leave.end_date}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">{leave.reason}</p>
                      </div>
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                        Approved
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Today's Attendance Modal/Panel */}
          {showTodayAttendance && (
            <div className="mb-8 bg-white rounded-lg border border-slate-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold font-heading tracking-tight">
                  Today's Attendance Status
                </h2>
                <Button variant="outline" size="sm" onClick={() => setShowTodayAttendance(false)}>
                  Close
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                {format(new Date(), 'EEEE, dd MMMM yyyy')} - Present: {todayAttendance.length} | Absent: {employees.length - todayAttendance.length}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {employees.map((emp) => {
                  const empAttendance = todayAttendance.find(a => a.user_id === emp.user_id);
                  const isPresent = !!empAttendance;
                  const checkInTime = empAttendance?.sessions?.[0]?.check_in;
                  
                  return (
                    <div 
                      key={emp.user_id}
                      className={`flex items-center justify-between p-3 rounded-md border ${
                        isPresent 
                          ? 'bg-emerald-50 border-emerald-200' 
                          : 'bg-rose-50 border-rose-200'
                      }`}
                    >
                      <div>
                        <p className="font-medium text-sm">{emp.name}</p>
                        <p className="text-xs text-muted-foreground">{emp.email}</p>
                      </div>
                      <div className="text-right">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                            isPresent
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-rose-100 text-rose-800'
                          }`}
                        >
                          {isPresent ? 'Present' : 'Absent'}
                        </span>
                        {isPresent && checkInTime && (
                          <p className="text-xs text-muted-foreground mt-1">
                            In: {formatTime(checkInTime)}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <Tabs defaultValue="leaves" className="space-y-6">
            <TabsList>
              <TabsTrigger value="leaves" data-testid="leaves-tab">Leave Requests</TabsTrigger>
              <TabsTrigger value="employees" data-testid="employees-tab">Employees</TabsTrigger>
              <TabsTrigger value="leave-summary" data-testid="leave-summary-tab">Leave Summary</TabsTrigger>
              <TabsTrigger value="attendance" data-testid="attendance-tab">Attendance</TabsTrigger>
              <TabsTrigger value="reports" data-testid="reports-tab">Reports</TabsTrigger>
              <TabsTrigger value="overtime" data-testid="overtime-tab">Overtime</TabsTrigger>
              <TabsTrigger value="holidays" data-testid="holidays-tab">Holidays</TabsTrigger>
              <TabsTrigger value="errors" data-testid="errors-tab">Errors</TabsTrigger>
            </TabsList>

            <TabsContent value="leaves">
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
                <h2 className="text-xl font-semibold font-heading tracking-tight mb-6">
                  Leave Requests
                </h2>
                <div className="space-y-4" data-testid="leave-requests-list">
                  {leaves.map((leave) => (
                    <div
                      key={leave.leave_id}
                      className="border border-slate-200 rounded-md p-4"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="font-semibold text-lg">{leave.user_name}</h3>
                          <p className="text-sm text-muted-foreground">{leave.user_email}</p>
                        </div>
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                            leave.status === 'approved'
                              ? 'bg-emerald-100 text-emerald-800'
                              : leave.status === 'rejected'
                              ? 'bg-rose-100 text-rose-800'
                              : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {leave.status}
                        </span>
                      </div>
                      <div className="mb-3">
                        <p className="font-medium">{leave.leave_type}</p>
                        <p className="text-sm text-muted-foreground">
                          {leave.start_date} to {leave.end_date}
                        </p>
                        <p className="text-sm mt-2">{leave.reason}</p>
                      </div>
                      {leave.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button
                            data-testid={`approve-leave-${leave.leave_id}`}
                            onClick={() => handleApprove(leave.leave_id)}
                            disabled={loading}
                            size="sm"
                            className="bg-success hover:bg-success/90"
                          >
                            <CheckCircle className="mr-1 h-4 w-4" />
                            Approve
                          </Button>
                          <Button
                            data-testid={`reject-leave-${leave.leave_id}`}
                            onClick={() => handleReject(leave.leave_id)}
                            disabled={loading}
                            size="sm"
                            variant="destructive"
                          >
                            <XCircle className="mr-1 h-4 w-4" />
                            Reject
                          </Button>
                        </div>
                      )}
                      <div className="flex gap-2 mt-2">
                        <Button
                          data-testid={`delete-leave-${leave.leave_id}`}
                          onClick={() => handleDeleteLeave(leave)}
                          disabled={loading}
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 text-xs"
                        >
                          <Trash2 className="mr-1 h-3 w-3" />
                          Delete Leave
                        </Button>
                      </div>
                    </div>
                  ))}
                  {leaves.length === 0 && (
                    <div className="text-center py-12">
                      <Calendar className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">No leave requests</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="employees">
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold font-heading tracking-tight">
                    Employees
                  </h2>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleMigrateLeaves}
                    disabled={loading}
                    className="text-amber-600 border-amber-300 hover:bg-amber-50"
                    data-testid="migrate-leaves-btn"
                  >
                    {loading ? 'Fixing...' : 'Fix All Leave Data'}
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="employees-table">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-sm font-semibold">Name</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold">Email</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold">Joined</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employees.map((emp) => (
                        <tr key={emp.user_id} className="border-b border-slate-100">
                          <td className="py-3 px-4">{emp.name}</td>
                          <td className="py-3 px-4 text-sm text-muted-foreground">{emp.email}</td>
                          <td className="py-3 px-4 text-sm">
                            {safeFormat(emp.created_at, 'MMM d, yyyy')}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => debugAndFixLeaves(emp.user_id, emp.name)}
                                className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 text-xs"
                                data-testid={`debug-leaves-${emp.user_id}`}
                              >
                                Fix Leaves
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeEmployee(emp.user_id, emp.name)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 text-xs"
                                data-testid={`remove-employee-${emp.user_id}`}
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Remove
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {employees.length === 0 && (
                        <tr>
                          <td colSpan="5" className="py-8 text-center text-muted-foreground">
                            No employees yet
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="leave-summary">
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold font-heading tracking-tight">
                    Employee Leave Summary — {leaveSummary?.year || new Date().getFullYear()}
                  </h2>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchLeaveSummary}
                    disabled={leaveSummaryLoading}
                    data-testid="refresh-leave-summary"
                  >
                    {leaveSummaryLoading ? 'Loading...' : 'Load Summary'}
                  </Button>
                </div>

                {!leaveSummary ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <p>Click "Load Summary" to view leave data for all employees</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="leave-summary-table">
                      <thead>
                        <tr className="border-b-2 border-slate-300">
                          <th className="text-left py-3 px-3 font-semibold">Employee</th>
                          <th className="text-center py-3 px-3 font-semibold">Starting Balance</th>
                          <th className="text-center py-3 px-3 font-semibold">Paid Leaves Taken</th>
                          <th className="text-center py-3 px-3 font-semibold">Overtime Adjustment</th>
                          <th className="text-center py-3 px-3 font-semibold">Carry Over Hours</th>
                          <th className="text-center py-3 px-3 font-semibold">Current Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leaveSummary.summary.map((emp) => (
                          <>
                            <tr
                              key={emp.user_id}
                              className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                              onClick={() => setExpandedEmployee(expandedEmployee === emp.user_id ? null : emp.user_id)}
                            >
                              <td className="py-3 px-3">
                                <div>
                                  <p className="font-medium">{emp.name}</p>
                                  <p className="text-xs text-muted-foreground">{emp.email}</p>
                                </div>
                              </td>
                              <td className="text-center py-3 px-3">
                                <span className="font-semibold text-slate-700">{emp.starting_leaves}</span>
                              </td>
                              <td className="text-center py-3 px-3">
                                <span className={`font-semibold ${emp.paid_leaves_taken > 0 ? 'text-amber-600' : 'text-slate-500'}`}>
                                  {emp.paid_leaves_taken > 0 ? `-${emp.paid_leaves_taken}` : '0'}
                                </span>
                              </td>
                              <td className="text-center py-3 px-3">
                                {emp.overtime_processed ? (
                                  <span className={`font-semibold ${emp.overtime_adjustment > 0 ? 'text-green-600' : emp.overtime_adjustment < 0 ? 'text-red-600' : 'text-slate-500'}`}>
                                    {emp.overtime_adjustment > 0 ? '+' : ''}{emp.overtime_adjustment}
                                  </span>
                                ) : (
                                  <span className="text-xs text-slate-400">Not processed</span>
                                )}
                              </td>
                              <td className="text-center py-3 px-3">
                                {emp.carry_over_hours !== 0 ? (
                                  <span className="text-xs text-amber-600">{emp.carry_over_hours >= 0 ? '+' : ''}{emp.carry_over_hours}h</span>
                                ) : (
                                  <span className="text-xs text-slate-400">0h</span>
                                )}
                              </td>
                              <td className="text-center py-3 px-3">
                                <span className={`text-lg font-bold ${emp.current_balance < 0 ? 'text-red-600' : emp.current_balance <= 3 ? 'text-amber-600' : 'text-green-600'}`}>
                                  {emp.current_balance}
                                </span>
                              </td>
                            </tr>
                            {expandedEmployee === emp.user_id && emp.leaves_detail.length > 0 && (
                              <tr key={`${emp.user_id}-detail`}>
                                <td colSpan="6" className="py-2 px-6 bg-slate-50">
                                  <p className="text-xs font-semibold text-slate-600 mb-2">Leave Details:</p>
                                  <div className="space-y-1">
                                    {emp.leaves_detail.map((l, i) => (
                                      <div key={i} className="flex items-center gap-4 text-xs text-slate-600">
                                        <span className="w-24 font-medium">{l.type}</span>
                                        <span>{l.start} to {l.end}</span>
                                        <span className="font-semibold">{l.days} day{l.days > 1 ? 's' : ''}</span>
                                        <span className="text-slate-400">{l.reason}</span>
                                      </div>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            )}
                            {expandedEmployee === emp.user_id && emp.leaves_detail.length === 0 && (
                              <tr key={`${emp.user_id}-empty`}>
                                <td colSpan="6" className="py-2 px-6 bg-slate-50">
                                  <p className="text-xs text-slate-400 text-center">No paid leaves taken this year</p>
                                </td>
                              </tr>
                            )}
                          </>
                        ))}
                      </tbody>
                    </table>
                    <div className="mt-4 text-xs text-muted-foreground text-center">
                      Click on any employee row to see leave details
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="attendance">
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold font-heading tracking-tight">
                    Attendance Records
                  </h2>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={openAddAttendance} className="mr-2">
                      <Plus className="h-4 w-4 mr-1" />
                      Add Attendance
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setShowBulkAttendance(true)} className="mr-2" data-testid="bulk-add-attendance-btn">
                      <Plus className="h-4 w-4 mr-1" />
                      Bulk Add Month
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => changeDate(-1)}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => handleDateChange(e.target.value)}
                      className="border border-slate-200 rounded px-3 py-1.5 text-sm"
                    />
                    <Button variant="outline" size="sm" onClick={() => changeDate(1)}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleDateChange(istTodayStr())}
                    >
                      Today
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Showing attendance for: <strong>{formatDate(selectedDate)}</strong>
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="attendance-table">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-sm font-semibold">Employee</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold">Check In</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold">Check Out</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold">Total Hours</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAttendance.map((record) => {
                        const sessions = record.sessions || [];
                        const firstCheckIn = sessions[0]?.check_in;
                        const lastCheckOut = sessions[sessions.length - 1]?.check_out;
                        const isActive = sessions.some(s => s.check_in && !s.check_out);
                        
                        // Use backend-computed total_minutes if available (most reliable)
                        let totalMinutes = record.total_minutes || 0;
                        
                        // Fallback: calculate from sessions in frontend
                        if (totalMinutes <= 0) {
                          sessions.forEach(session => {
                            if (session.check_in && session.check_out) {
                              try {
                                const checkIn = new Date(session.check_in);
                                const checkOut = new Date(session.check_out);
                                if (!isNaN(checkIn.getTime()) && !isNaN(checkOut.getTime())) {
                                  totalMinutes += (checkOut - checkIn) / (1000 * 60);
                                }
                              } catch(e) {}
                            }
                          });
                        }
                        
                        // Fallback: parse total_duration string from backend (format: "H:MM:SS")
                        if (totalMinutes <= 0 && record.total_duration && record.total_duration !== '0:00:00') {
                          try {
                            const parts = record.total_duration.split(':');
                            if (parts.length >= 2) {
                              totalMinutes = parseInt(parts[0]) * 60 + parseInt(parts[1]);
                            }
                          } catch(e) {}
                        }
                        
                        const hours = Math.floor(totalMinutes / 60);
                        const mins = Math.round(totalMinutes % 60);
                        
                        return (
                          <tr key={record.user_id + record.date} className="border-b border-slate-100">
                            <td className="py-3 px-4">
                              <div>
                                <div className="font-medium">{record.user_name || 'Unknown'}</div>
                                {sessions.length > 1 && (
                                  <div className="text-xs text-muted-foreground">{sessions.length} sessions</div>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-sm">{formatTime(firstCheckIn)}</td>
                            <td className="py-3 px-4 text-sm">{formatTime(lastCheckOut)}</td>
                            <td className="py-3 px-4 text-sm">
                              {totalMinutes > 0 ? `${hours}h ${mins}m` : '-'}
                            </td>
                            <td className="py-3 px-4">
                              <span
                                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                                  isActive
                                    ? 'bg-amber-100 text-amber-800'
                                    : 'bg-emerald-100 text-emerald-800'
                                }`}
                              >
                                {isActive ? 'Active' : 'Completed'}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditAttendance(record, sessions.length - 1)}
                                  className="h-8 w-8 p-0"
                                  title="Edit Attendance"
                                >
                                  <Edit className="h-4 w-4 text-blue-600" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteSession(record, sessions.length - 1)}
                                  className="h-8 w-8 p-0"
                                  title="Delete Session"
                                >
                                  <Trash2 className="h-4 w-4 text-red-600" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {filteredAttendance.length === 0 && (
                        <tr>
                          <td colSpan="6" className="py-8 text-center text-muted-foreground">
                            No attendance records for {formatDate(selectedDate)}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="reports">
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-semibold font-heading tracking-tight">
                      Payroll Reports
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      Generate and download attendance reports for payroll
                    </p>
                  </div>
                  <FileText className="h-8 w-8 text-accent" />
                </div>

                <div className="mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={useCustomDateRange}
                      onChange={(e) => setUseCustomDateRange(e.target.checked)}
                      className="rounded border-slate-300"
                    />
                    <span className="text-sm font-medium">Use custom date range</span>
                  </label>
                </div>

                <div className="flex flex-wrap items-end gap-4 mb-6 p-4 bg-slate-50 rounded-lg">
                  {!useCustomDateRange ? (
                    <>
                      <div>
                        <label className="block text-sm font-medium mb-1">Month</label>
                        <select
                          value={reportMonth}
                          onChange={(e) => setReportMonth(parseInt(e.target.value))}
                          className="border border-slate-200 rounded px-3 py-2 text-sm min-w-[140px]"
                        >
                          {monthNames.map((month, index) => (
                            <option key={index} value={index + 1}>{month}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Year</label>
                        <select
                          value={reportYear}
                          onChange={(e) => setReportYear(parseInt(e.target.value))}
                          className="border border-slate-200 rounded px-3 py-2 text-sm"
                        >
                          {[2024, 2025, 2026, 2027].map((year) => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="block text-sm font-medium mb-1">Start Date</label>
                        <input
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          className="border border-slate-200 rounded px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">End Date</label>
                        <input
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          className="border border-slate-200 rounded px-3 py-2 text-sm"
                        />
                      </div>
                    </>
                  )}
                  <Button onClick={generateReport} disabled={reportLoading}>
                    {reportLoading ? 'Generating...' : 'Generate Report'}
                  </Button>
                  {reportData && (
                    <Button onClick={downloadReport} variant="outline" className="gap-2">
                      <Download className="h-4 w-4" />
                      Download CSV
                    </Button>
                  )}
                </div>

                {reportData && (
                  <div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-600 font-medium">Working Days</p>
                        <p className="text-2xl font-bold text-blue-800">{reportData.working_days_in_range || reportData.working_days_in_month}</p>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg">
                        <p className="text-sm text-green-600 font-medium">Company Holidays</p>
                        <p className="text-2xl font-bold text-green-800">{reportData.company_holidays}</p>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg">
                        <p className="text-sm text-purple-600 font-medium">Total Employees</p>
                        <p className="text-2xl font-bold text-purple-800">{reportData.total_employees}</p>
                      </div>
                      <div className="p-4 bg-amber-50 rounded-lg">
                        <p className="text-sm text-amber-600 font-medium">Report Period</p>
                        <p className="text-lg font-bold text-amber-800">
                          {useCustomDateRange 
                            ? `${startDate} to ${endDate}` 
                            : `${monthNames[reportMonth - 1]} ${reportYear}`}
                        </p>
                      </div>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200 bg-slate-50">
                            <th className="text-left py-3 px-3 font-semibold">Name</th>
                            <th className="text-center py-3 px-2 font-semibold">Days Worked</th>
                            <th className="text-center py-3 px-2 font-semibold">Hours Worked</th>
                            <th className="text-center py-3 px-2 font-semibold">Paid Leaves Taken</th>
                            <th className="text-center py-3 px-2 font-semibold">Unclaimed Paid Leaves</th>
                            <th className="text-center py-3 px-2 font-semibold">Holiday Leaves Taken</th>
                            <th className="text-center py-3 px-2 font-semibold">Unclaimed Holiday Leaves</th>
                            <th className="text-center py-3 px-2 font-semibold">Download</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.report.map((row, index) => {
                            const totalExpectedHours = reportData.total_expected_hours || (reportData.working_days_in_month * 8);
                            
                            return (
                              <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                                <td className="py-3 px-3 font-medium">{row.employee_name}</td>
                                <td className="text-center py-3 px-2">{row.days_worked}/{row.total_working_days}</td>
                                <td className="text-center py-3 px-2">{row.total_hours_worked}/{totalExpectedHours}h</td>
                                <td className="text-center py-3 px-2">{row.paid_leaves_used}</td>
                                <td className="text-center py-3 px-2">{row.unclaimed_paid_leaves}</td>
                                <td className="text-center py-3 px-2">{row.holiday_leaves_taken}</td>
                                <td className="text-center py-3 px-2">0</td>
                                <td className="text-center py-3 px-2">
                                  <button
                                    onClick={() => downloadEmployeeAttendance(row.user_id, row.employee_name)}
                                    className="text-primary hover:text-primary/80 p-1"
                                    title="Download"
                                  >
                                    <Download className="h-4 w-4" />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {!reportData && (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Select a month and click "Generate Report" to view the attendance summary</p>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="overtime">
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-semibold font-heading tracking-tight">
                      Overtime / Undertime Adjustment
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      Every 6 extra hours = +1 paid leave | Every 6 hours short = -1 paid leave
                    </p>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-amber-800">
                    <strong>Note:</strong> Start processing overtime from <strong>April 2026</strong> onwards. 
                    March 2026 data is incomplete (first 12 days were not tracked).
                  </p>
                </div>

                <div className="flex items-center gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                    <select
                      value={overtimeMonth}
                      onChange={(e) => setOvertimeMonth(parseInt(e.target.value))}
                      className="border border-slate-300 rounded-md px-3 py-2"
                    >
                      {monthNames.map((name, index) => (
                        <option key={index} value={index + 1}>{name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                    <select
                      value={overtimeYear}
                      onChange={(e) => setOvertimeYear(parseInt(e.target.value))}
                      className="border border-slate-300 rounded-md px-3 py-2"
                    >
                      {[2024, 2025, 2026, 2027].map((year) => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-end gap-2">
                    <Button onClick={fetchOvertimePreview} disabled={overtimeLoading}>
                      {overtimeLoading ? 'Loading...' : 'Preview'}
                    </Button>
                    {overtimePreview && (
                      <Button onClick={processOvertime} disabled={overtimeLoading} variant="default" className="bg-green-600 hover:bg-green-700">
                        Apply Adjustments
                      </Button>
                    )}
                  </div>
                </div>

                {overtimePreview && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <p className="text-sm text-blue-600">Working Days</p>
                        <p className="text-2xl font-bold text-blue-800">{overtimePreview.working_days}</p>
                      </div>
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <p className="text-sm text-blue-600">Expected Hours</p>
                        <p className="text-2xl font-bold text-blue-800">{overtimePreview.expected_hours_per_employee}h</p>
                      </div>
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <p className="text-sm text-blue-600">Threshold</p>
                        <p className="text-2xl font-bold text-blue-800">{overtimePreview.threshold_hours}h = ±1 leave</p>
                      </div>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Hours Worked</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Expected</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Difference</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Carry Over</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Balance</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Leave Adjustment</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">New Carry Over</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {overtimePreview.preview.map((emp) => (
                            <tr key={emp.user_id} className={emp.already_processed ? 'bg-gray-50' : ''}>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="font-medium text-gray-900">{emp.employee_name}</div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                                {emp.hours_worked}h
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-500">
                                {emp.expected_hours}h
                              </td>
                              <td className={`px-4 py-3 whitespace-nowrap text-right text-sm font-medium ${emp.hours_difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {emp.hours_difference >= 0 ? '+' : ''}{emp.hours_difference}h
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-500">
                                {emp.previous_carry_over}h
                              </td>
                              <td className={`px-4 py-3 whitespace-nowrap text-right text-sm font-medium ${emp.total_balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {emp.total_balance >= 0 ? '+' : ''}{emp.total_balance}h
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                {emp.potential_leave_adjustment !== 0 ? (
                                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${emp.potential_leave_adjustment > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                    {emp.potential_leave_adjustment > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                                    {emp.potential_leave_adjustment > 0 ? '+' : ''}{emp.potential_leave_adjustment} leave
                                  </span>
                                ) : (
                                  <span className="text-gray-400 text-xs">No change</span>
                                )}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-500">
                                {emp.remaining_carry_over}h
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                {emp.already_processed ? (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                    Processed
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                    Pending
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                {emp.already_processed ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => resetAndReprocessOvertime(emp.user_id, emp.employee_name)}
                                    disabled={overtimeLoading}
                                    className="text-xs text-amber-600 border-amber-300 hover:bg-amber-50"
                                    data-testid={`reset-overtime-${emp.user_id}`}
                                  >
                                    Reset & Reprocess
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => processIndividualOvertime(emp.user_id, emp.employee_name)}
                                    disabled={overtimeLoading}
                                    className="text-xs"
                                    data-testid={`apply-overtime-${emp.user_id}`}
                                  >
                                    Apply
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {!overtimePreview && (
                  <div className="text-center py-12 text-gray-500">
                    <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>Select a month and click "Preview" to see overtime adjustments</p>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="holidays">
              <HolidayManager />
            </TabsContent>

            <TabsContent value="errors">
              <ClientErrorsLog />
            </TabsContent>
          </Tabs>
        </motion.div>
      </main>

      {/* Edit Attendance Modal */}
      {showEditAttendance && editingRecord && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Edit Attendance</h3>
            <div className="mb-4">
              <p className="text-sm text-muted-foreground mb-1">Employee</p>
              <p className="font-medium">{editingRecord.user_name}</p>
            </div>
            <div className="mb-4">
              <p className="text-sm text-muted-foreground mb-1">Date</p>
              <p className="font-medium">{formatDate(editingRecord.date)}</p>
            </div>
            <div className="mb-4">
              <label className="text-sm text-muted-foreground mb-1 block">
                Session ({editingRecord.sessions?.length || 0} total)
              </label>
              <select
                value={editSessionIndex}
                onChange={(e) => {
                  const idx = parseInt(e.target.value);
                  setEditSessionIndex(idx);
                  const session = editingRecord.sessions[idx];
                  if (session?.check_in) {
                    setEditCheckIn(new Date(session.check_in).toISOString().slice(0, 16));
                  } else {
                    setEditCheckIn('');
                  }
                  if (session?.check_out) {
                    setEditCheckOut(new Date(session.check_out).toISOString().slice(0, 16));
                  } else {
                    setEditCheckOut('');
                  }
                }}
                className="w-full border border-slate-200 rounded px-3 py-2"
              >
                {editingRecord.sessions?.map((s, i) => (
                  <option key={i} value={i}>
                    Session {i + 1} - {s.check_in ? new Date(s.check_in).toLocaleTimeString('en-IN', {hour: '2-digit', minute: '2-digit'}) : 'No check-in'} 
                    {s.check_out ? ` to ${new Date(s.check_out).toLocaleTimeString('en-IN', {hour: '2-digit', minute: '2-digit'})}` : ' (Active)'}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-4">
              <label className="text-sm text-muted-foreground mb-1 block">Check In</label>
              <input
                type="datetime-local"
                value={editCheckIn}
                onChange={(e) => setEditCheckIn(e.target.value)}
                className="w-full border border-slate-200 rounded px-3 py-2"
              />
            </div>
            <div className="mb-6">
              <label className="text-sm text-muted-foreground mb-1 block">Check Out</label>
              <input
                type="datetime-local"
                value={editCheckOut}
                onChange={(e) => setEditCheckOut(e.target.value)}
                className="w-full border border-slate-200 rounded px-3 py-2"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setShowEditAttendance(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateAttendance} disabled={loading}>
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Attendance Modal */}
      {showAddAttendance && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Add Attendance</h3>
            <div className="mb-4">
              <label className="text-sm text-muted-foreground mb-1 block">Employee</label>
              <select
                value={addUserId}
                onChange={(e) => setAddUserId(e.target.value)}
                className="w-full border border-slate-200 rounded px-3 py-2"
              >
                <option value="">Select Employee</option>
                {employees.map((emp) => (
                  <option key={emp.user_id} value={emp.user_id}>
                    {emp.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-4">
              <label className="text-sm text-muted-foreground mb-1 block">Date</label>
              <input
                type="date"
                value={addDate}
                onChange={(e) => setAddDate(e.target.value)}
                className="w-full border border-slate-200 rounded px-3 py-2"
              />
            </div>
            <div className="mb-4">
              <label className="text-sm text-muted-foreground mb-1 block">Check In</label>
              <input
                type="datetime-local"
                value={addCheckIn}
                onChange={(e) => setAddCheckIn(e.target.value)}
                className="w-full border border-slate-200 rounded px-3 py-2"
              />
            </div>
            <div className="mb-6">
              <label className="text-sm text-muted-foreground mb-1 block">Check Out (optional)</label>
              <input
                type="datetime-local"
                value={addCheckOut}
                onChange={(e) => setAddCheckOut(e.target.value)}
                className="w-full border border-slate-200 rounded px-3 py-2"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setShowAddAttendance(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddAttendance} disabled={loading}>
                {loading ? 'Adding...' : 'Add Attendance'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Add Attendance Modal */}
      {showBulkAttendance && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Bulk Add Monthly Attendance</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Add attendance for all missing weekdays in a month with fixed timings.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Employee</label>
                <select
                  value={bulkUserId}
                  onChange={(e) => setBulkUserId(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  data-testid="bulk-employee-select"
                >
                  <option value="">Select Employee</option>
                  {employees.map(emp => (
                    <option key={emp.user_id} value={emp.user_id}>{emp.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Month</label>
                  <select
                    value={bulkMonth}
                    onChange={(e) => setBulkMonth(Number(e.target.value))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    {monthNames.map((name, i) => (
                      <option key={i} value={i + 1}>{name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Year</label>
                  <input
                    type="number"
                    value={bulkYear}
                    onChange={(e) => setBulkYear(Number(e.target.value))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">From Date <span className="text-xs text-muted-foreground">(optional)</span></label>
                  <input
                    type="date"
                    value={bulkFromDate}
                    onChange={(e) => setBulkFromDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="Leave empty for full month"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">To Date <span className="text-xs text-muted-foreground">(optional)</span></label>
                  <input
                    type="date"
                    value={bulkToDate}
                    onChange={(e) => setBulkToDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="Leave empty for full month"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground -mt-2">Leave dates empty to add for the entire month</p>

              <div className="border rounded-lg p-3 bg-blue-50">
                <p className="text-sm font-medium mb-2 text-blue-800">Session 1 (Morning)</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs mb-1">Check In (IST)</label>
                    <input type="time" value={bulkSession1In} onChange={(e) => setBulkSession1In(e.target.value)}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs mb-1">Check Out (IST)</label>
                    <input type="time" value={bulkSession1Out} onChange={(e) => setBulkSession1Out(e.target.value)}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                  </div>
                </div>
              </div>

              <div className="border rounded-lg p-3 bg-indigo-50">
                <p className="text-sm font-medium mb-2 text-indigo-800">Session 2 (Afternoon)</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs mb-1">Check In (IST)</label>
                    <input type="time" value={bulkSession2In} onChange={(e) => setBulkSession2In(e.target.value)}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs mb-1">Check Out (IST)</label>
                    <input type="time" value={bulkSession2Out} onChange={(e) => setBulkSession2Out(e.target.value)}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                  </div>
                </div>
              </div>

              <div className="border rounded-lg p-3 bg-amber-50">
                <p className="text-sm font-medium mb-2 text-amber-800">Sunday Work (Optional)</p>
                <div className="mb-2">
                  <label className="block text-xs mb-1">Sunday dates to include (comma-separated, e.g., 2026-04-19)</label>
                  <input type="text" value={bulkIncludeSundays} onChange={(e) => setBulkIncludeSundays(e.target.value)}
                    placeholder="2026-04-19, 2026-04-26"
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                </div>
                {bulkIncludeSundays && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs mb-1">Sunday Check In (IST)</label>
                      <input type="time" value={bulkSundayIn} onChange={(e) => setBulkSundayIn(e.target.value)}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs mb-1">Sunday Check Out (IST)</label>
                      <input type="time" value={bulkSundayOut} onChange={(e) => setBulkSundayOut(e.target.value)}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowBulkAttendance(false)}>Cancel</Button>
              <Button onClick={handleBulkAddAttendance} disabled={bulkLoading || !bulkUserId} data-testid="bulk-add-submit-btn">
                {bulkLoading ? 'Adding...' : 'Add Bulk Attendance'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;